import { box, type Box } from '../geometry/box';
import type { FrameLayout, Size } from '../geometry/framing';
import type { ScreenActivity } from './activity';

/**
 * Automatic zoom for screen recordings: while the work stays in a small part of
 * the screen (typing in a field, clicking through a menu), the picture eases in
 * on it, and it eases back out when the whole screen changes (a new page, a
 * scroll). Browsers don't share where the pointer is in other windows, so the
 * focus comes from where the picture changes.
 */

export interface ZoomSettings {
  readonly enabled: boolean;
  /** Largest zoom, as a magnification (1.8 = 180 %). */
  readonly amount: number;
  /** Zoom moments the person turned off, by id. */
  readonly skipped: readonly string[];
}

export const ZOOM_AMOUNT = { min: 1.3, max: 2.5 } as const;
export const DEFAULT_ZOOM_SETTINGS: ZoomSettings = { enabled: false, amount: 1.8, skipped: [] };

export interface ZoomSegment {
  readonly id: string;
  /** Fully zoomed in from `start` to `end` (seconds); easing happens just outside. */
  readonly start: number;
  readonly end: number;
  /** Centre of attention, normalized. */
  readonly focus: { readonly x: number; readonly y: number };
  readonly scale: number;
}

export interface ZoomState {
  readonly scale: number;
  readonly focus: { readonly x: number; readonly y: number };
}

/** Changes larger than this share of the screen are a new view, not focused work. */
const BUSY_RATIO = 0.12;
/** Below this the change is noise; a few typed characters are just above it (a caret blink is dropped earlier). */
const MIN_RATIO = 0.0002;
/** Zooming in less than this isn't worth the motion. */
const MIN_SCALE = 1.25;
/** Room kept around the active area. */
const PADDING = 1.5;
/** Quiet moments shorter than this don't end a zoom. */
const QUIET_BRIDGE_SEC = 1.6;
const MIN_SEGMENT_SEC = 1.6;
/** Easing time into and out of a zoom. */
export const ZOOM_RAMP_SEC = 0.6;

const union = (a: Box, b: Box): Box => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return box(x, y, Math.max(a.x + a.w, b.x + b.w) - x, Math.max(a.y + a.h, b.y + b.h) - y);
};

/** How far one can zoom into `area` with padding, given the picture's aspect. */
const fitScale = (area: Box, amount: number): number => Math.min(amount, 1 / (area.w * PADDING), 1 / (area.h * PADDING));

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function segmentFrom(area: Box, start: number, end: number, amount: number): ZoomSegment {
  const scale = fitScale(area, amount);
  const half = 0.5 / scale;
  return {
    id: `zoom-${Math.round(start * 10)}`,
    start,
    end,
    focus: { x: clamp(area.x + area.w / 2, half, 1 - half), y: clamp(area.y + area.h / 2, half, 1 - half) },
    scale,
  };
}

const overlaps = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** A moment and place to leave out of the zoom (a notification appearing or leaving). */
export interface ZoomExclusion {
  readonly start: number;
  readonly end: number;
  readonly box: Box;
}

/**
 * Finds the moments worth zooming into. Notifications popping up or leaving are
 * left out: zooming in on one would put exactly what should stay private centre stage.
 */
export function planZoom(activity: ScreenActivity, amount: number, exclude: readonly ZoomExclusion[] = []): ZoomSegment[] {
  const excluded = (t: number, area: Box) =>
    exclude.some((hit) => (Math.abs(t - hit.start) <= 0.6 || Math.abs(t - hit.end) <= 0.6) && overlaps(area, hit.box));
  const segments: ZoomSegment[] = [];
  let area: Box | null = null;
  let first = 0;
  let last = 0;
  const close = () => {
    if (area && last - first + activity.interval >= MIN_SEGMENT_SEC) segments.push(segmentFrom(area, first - activity.interval, last, amount));
    area = null;
  };
  for (const sample of activity.samples) {
    if (sample.ratio >= BUSY_RATIO) {
      close();
      continue;
    }
    if (!sample.box || sample.ratio < MIN_RATIO || excluded(sample.t, sample.box)) {
      if (area && sample.t - last > QUIET_BRIDGE_SEC) close();
      continue;
    }
    const grown: Box = area ? union(area, sample.box) : sample.box;
    if (fitScale(grown, amount) < MIN_SCALE) {
      // The work moved somewhere else: the current zoom ends and a new one may start here.
      close();
      area = fitScale(sample.box, amount) >= MIN_SCALE ? sample.box : null;
      first = last = sample.t;
      continue;
    }
    if (!area) first = sample.t;
    area = grown;
    last = sample.t;
  }
  close();
  return segments;
}

const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/** Zooms closer than this stay zoomed in and pan from one to the next instead of easing out and back in. */
const JOIN_GAP_SEC = ZOOM_RAMP_SEC * 2 + 0.4;

const scaled = (segment: ZoomSegment, amount: number): ZoomState => ({ scale: 1 + (segment.scale - 1) * amount, focus: segment.focus });

const blend = (from: ZoomSegment, to: ZoomSegment, share: number): ZoomState => ({
  scale: from.scale + (to.scale - from.scale) * share,
  focus: { x: from.focus.x + (to.focus.x - from.focus.x) * share, y: from.focus.y + (to.focus.y - from.focus.y) * share },
});

/** The zoom at a moment (segments sorted by time); null when not zoomed. */
export function zoomAt(segments: readonly ZoomSegment[], t: number): ZoomState | null {
  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index]!;
    if (t < segment.start - ZOOM_RAMP_SEC) return null;
    const previous = segments[index - 1];
    const next = segments[index + 1];
    const joinedBefore = previous !== undefined && segment.start - previous.end <= JOIN_GAP_SEC;
    const joinedAfter = next !== undefined && next.start - segment.end <= JOIN_GAP_SEC;
    if (t < segment.start) return joinedBefore ? null : scaled(segment, ease(1 - (segment.start - t) / ZOOM_RAMP_SEC));
    if (t <= segment.end) return scaled(segment, 1);
    if (joinedAfter && t < next.start) return blend(segment, next, ease((t - segment.end) / (next.start - segment.end)));
    if (!joinedAfter && t <= segment.end + ZOOM_RAMP_SEC) {
      const amount = ease(1 - (t - segment.end) / ZOOM_RAMP_SEC);
      return amount > 1e-6 ? scaled(segment, amount) : null;
    }
  }
  return null;
}

/**
 * The layout with the zoom applied: a smaller part of the source fills the
 * same spot, centred on the focus and never leaving the visible area.
 */
export function applyZoom(layout: FrameLayout, display: Size, zoom: ZoomState | null): FrameLayout {
  if (!zoom || zoom.scale <= 1.001) return layout;
  const s = layout.sourceRect;
  const w = s.w / zoom.scale;
  const h = s.h / zoom.scale;
  const x = clamp(zoom.focus.x * display.width - w / 2, s.x, s.x + s.w - w);
  const y = clamp(zoom.focus.y * display.height - h / 2, s.y, s.y + s.h - h);
  return { ...layout, sourceRect: box(x, y, w, h) };
}

/** Zoom moments that apply: on, and not turned off one by one. */
export const activeZoomSegments = (segments: readonly ZoomSegment[], settings: ZoomSettings): ZoomSegment[] =>
  settings.enabled ? segments.filter((segment) => !settings.skipped.includes(segment.id)) : [];

/** Restores stored settings defensively. */
export function parseZoomSettings(value: unknown): ZoomSettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof ZoomSettings, unknown>>;
  return {
    enabled: raw.enabled === true,
    amount: typeof raw.amount === 'number' && Number.isFinite(raw.amount) ? clamp(raw.amount, ZOOM_AMOUNT.min, ZOOM_AMOUNT.max) : DEFAULT_ZOOM_SETTINGS.amount,
    skipped: Array.isArray(raw.skipped) ? raw.skipped.filter((id): id is string => typeof id === 'string') : [],
  };
}
